import { z } from 'zod'
import { AsyncFn, emailUtils } from '../../common'
import { guidSchema } from '../../contracts/_common'
import { AddressDtoSchema } from '../Address/Address'
import { CompanyGuid, ForCompany } from '../Company/Company'
import { bzOptional } from '../common-schemas'
import { emailAddressValueSchema } from '../Email/EmailTypes'
import { telephoneNumberSchema } from '../PhoneNumbers/Phone'

export const InstantBookingIdentityResolveInputSchema = z.object({
  callerPhone: bzOptional(telephoneNumberSchema),
  callerEmail: bzOptional(
    z.preprocess(
      value => (typeof value === 'string' && value.trim().length > 0 ? emailUtils.canonicalize(value) : value),
      emailAddressValueSchema,
    ),
  ),
  callerFirstName: bzOptional(z.string().min(1)),
  callerLastName: bzOptional(z.string().min(1)),
  callerName: bzOptional(z.string().min(1)),
  confirmedServiceAddress: bzOptional(AddressDtoSchema),
  includeArchived: bzOptional(z.boolean()),
})

export type InstantBookingIdentityResolveInput = z.infer<typeof InstantBookingIdentityResolveInputSchema>

export const InstantBookingIdentityAccountMatchReasonSchema = z.enum([
  'phone',
  'email',
  'name',
  'address_exact',
  'address_line1_zip',
  'subpremise_missing',
])

export type InstantBookingIdentityAccountMatchReason = z.infer<typeof InstantBookingIdentityAccountMatchReasonSchema>

export const InstantBookingIdentityContactMatchReasonSchema = z.enum(['phone', 'email', 'name'])
export type InstantBookingIdentityContactMatchReason = z.infer<typeof InstantBookingIdentityContactMatchReasonSchema>

export const InstantBookingIdentityLocationMatchReasonSchema = z.enum([
  'address_exact',
  'address_line1_zip',
  'subpremise_missing',
])

export type InstantBookingIdentityLocationMatchReason = z.infer<typeof InstantBookingIdentityLocationMatchReasonSchema>

export const InstantBookingIdentityAddressMatchStrengthSchema = z.enum([
  'exact',
  'line1_zip',
  'subpremise_missing',
  'none',
])

export type InstantBookingIdentityAddressMatchStrength = z.infer<
  typeof InstantBookingIdentityAddressMatchStrengthSchema
>

export const InstantBookingIdentityContactCandidateSchema = z.object({
  contactGuid: guidSchema,
  firstName: z.string(),
  lastName: z.string(),
  primary: z.boolean(),
  archived: z.boolean(),
  matchReasons: z.array(InstantBookingIdentityContactMatchReasonSchema),
})

export type InstantBookingIdentityContactCandidate = z.infer<typeof InstantBookingIdentityContactCandidateSchema>

export const InstantBookingIdentityLocationCandidateSchema = z.object({
  locationGuid: guidSchema,
  displayLabel: z.string(),
  address: AddressDtoSchema,
  archived: z.boolean(),
  addressMatchStrength: InstantBookingIdentityAddressMatchStrengthSchema,
  matchReasons: z.array(InstantBookingIdentityLocationMatchReasonSchema),
})

export type InstantBookingIdentityLocationCandidate = z.infer<typeof InstantBookingIdentityLocationCandidateSchema>

export const InstantBookingIdentityAccountCandidateSchema = z.object({
  accountGuid: guidSchema,
  displayLabel: z.string(),
  referenceNumber: bzOptional(z.string()),
  customerNumber: bzOptional(z.string()),
  archived: z.boolean(),
  doNotService: bzOptional(z.boolean()),
  doNotServiceReason: bzOptional(z.string()),
  matchReasons: z.array(InstantBookingIdentityAccountMatchReasonSchema),
  contacts: z.array(InstantBookingIdentityContactCandidateSchema),
  locations: z.array(InstantBookingIdentityLocationCandidateSchema),
})

export type InstantBookingIdentityAccountCandidate = z.infer<typeof InstantBookingIdentityAccountCandidateSchema>

export const InstantBookingIdentityAmbiguityReasonSchema = z.enum([
  'multiple_accounts',
  'multiple_contacts',
  'multiple_locations',
  'address_not_exact',
  'no_matching_location',
])

export type InstantBookingIdentityAmbiguityReason = z.infer<typeof InstantBookingIdentityAmbiguityReasonSchema>

const InstantBookingIdentityResolutionBaseSchema = z.object({
  candidateAccounts: z.array(InstantBookingIdentityAccountCandidateSchema),
})

// Ambiguity reasons are only valid when the outcome is ambiguous.
export const InstantBookingIdentityResolutionSchema = z.discriminatedUnion('outcome', [
  InstantBookingIdentityResolutionBaseSchema.extend({
    outcome: z.literal('no_match'),
    ambiguityReason: z.never().optional(),
  }),
  InstantBookingIdentityResolutionBaseSchema.extend({
    outcome: z.literal('single_candidate'),
    ambiguityReason: z.never().optional(),
  }),
  InstantBookingIdentityResolutionBaseSchema.extend({
    outcome: z.literal('ambiguous'),
    ambiguityReason: InstantBookingIdentityAmbiguityReasonSchema,
  }),
])

export type InstantBookingIdentityResolution = z.infer<typeof InstantBookingIdentityResolutionSchema>

export type InstantBookingIdentityResolverFn = AsyncFn<
  ForCompany<InstantBookingIdentityResolveInput>,
  InstantBookingIdentityResolution
>

export type InstantBookingIdentityCompanyGuid = CompanyGuid
